import { NavigationGuardNext, RouteLocationNormalized, RouteRecordRaw } from 'vue-router';
import { Error401, Error404, Error503, UnsupportedBrowser } from '@/views/ErrorPage';
import { i18n } from '@/i18n/i18n';
import { FEATURES } from '@/constants';

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'login',
    component: () => import(/* webpackChunkName: "login" */ '../views/LoginPage.vue'),
    meta: { authRequired: false, loginPage: true, title: i18n.global.t('platform.login.title') }
  },
  {
    path: '/new-password',
    name: 'new-password',
    props: true,
    beforeEnter: (to: RouteLocationNormalized, _from: RouteLocationNormalized, next: NavigationGuardNext) => {
      if (!to.query.token) {
        return next({ name: 'login' });
      }

      return next();
    },
    component: () => import(/* webpackChunkName: "new-password" */ '../views/users/NewPasswordPage.vue'),
    meta: {
      authRequired: false,
      title: i18n.global.t('platform.reset.new-password')
    }
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import(/* webpackChunkName: "reset-password" */ '../views/users/ResetPasswordPage.vue'),
    meta: {
      authRequired: false,
      title: i18n.global.t('platform.profile.reset-password')
    }
  },
  {
    path: '/weak-password',
    name: 'weak-password',
    props: true,
    component: () => import(/* webpackChunkName: "weak-password" */ '../views/users/WeakPasswordPage.vue'),
    meta: { authRequired: false, title: i18n.global.t('platform.profile.weak-password') },
    beforeEnter: (to: RouteLocationNormalized, _from: RouteLocationNormalized, next: NavigationGuardNext) => {
      if (!to.params.email) {
        return next({ name: 'login' });
      }
      return next();
    }
  },
  {
    path: '/user-agreement',
    name: 'user-agreement',
    component: () => import(/* webpackChunkName: "agreement" */ '../views/UserAgreementPage.vue'),
    meta: { authRequired: true, title: i18n.global.t('platform.user-agreement.title') }
  },
  {
    path: '/admin',
    name: 'domain-admin',
    component: () => import(/* webpackChunkName: "domain-admin" */ '../views/admin/AdminLayout.vue'),
    meta: { authRequired: true, isAdmin: true },
    redirect: '/admin/users',
    children: [
      {
        path: 'account-settings',
        name: 'domain-admin-settings',
        component: () => import(/* webpackChunkName: "settings" */ '../views/settings/SettingsPage.vue'),
        meta: { authRequired: true, isAdmin: true },
        redirect: '/admin/account-settings/profile',
        children: [
          {
            path: 'profile',
            name: 'domain-admin-profile',
            component: () => import(/* webpackChunkName: "profile" */ '../views/settings/ProfilePage.vue'),
            meta: {
              authRequired: true,
              title: i18n.global.t('platform.settings.profile'),
              isUserSettings: true,
              isAdmin: true
            }
          },
          {
            path: 'account-setting',
            name: 'domain-admin-account-setting',
            component: () => import(/* webpackChunkName: "account-setting" */ '../views/settings/AccountSettingPage.vue'),
            meta: {
              authRequired: true,
              title: i18n.global.t('platform.settings.account-setting'),
              isUserSettings: true,
              isAdmin: true
            }
          }
        ]
      },
      {
        path: 'users',
        name: 'domain-admin-users',
        component: () => import(/* webpackChunkName: "users" */ '../views/admin/UsersPage.vue'),
        meta: { authRequired: true, title: i18n.global.t('platform.user.users'), isAdmin: true }
      },
      {
        path: 'organisations',
        name: 'domain-admin-organisations',
        component: () => import(/* webpackChunkName: "organisations" */ '../views/admin/OrganisationsPage.vue'),
        meta: {
          authRequired: true,
          title: i18n.global.t('platform.org-unit.org-units'),
          isAdmin: true
        }
      },
      {
        path: 'organisations/:organisationId',
        name: 'domain-admin-organisation',
        props: true,
        component: () => import(/* webpackChunkName: "org" */ '../views/admin/OrganisationLayout.vue'),
        meta: { authRequired: true, isAdmin: true, title: i18n.global.t('platform.org-unit.org-unit') },
        redirect: (to) => ({
          name: 'domain-admin-organisation-details',
          params: { organisationId: to.params.organisationId },
          query: to.query
        }),
        children: [
          {
            path: 'details',
            component: () => import(/* webpackChunkName: "org-details" */ '../views/admin/OrganisationDetailPage.vue'),
            props: true,
            name: 'domain-admin-organisation-details',
            meta: {
              authRequired: true,
              title: i18n.global.t('platform.org-unit.org-unit'),
              isAdmin: true
            }
          },
          {
            path: 'settings',
            component: () =>
              import(/* webpackChunkName: "org-settings" */ '../views/admin/OrganisationSettingsPage.vue'),
            props: true,
            meta: {
              authRequired: true,
              title: i18n.global.t('platform.org-unit.settings'),
              isAdmin: true
            }
          },
          {
            path: 'preferences',
            component: () =>
              import(/* webpackChunkName: "org-settings" */ '../views/admin/OrganisationPreferencesPage.vue'),
            props: true,
            meta: {
              authRequired: true,
              title: i18n.global.t('platform.settings.preferences'),
              isAdmin: true
            }
          },
          {
            path: 'users',
            name: 'domain-admin-organisation-users',
            component: () => import(/* webpackChunkName: "users" */ '../views/users/UsersPage.vue'),
            meta: {
              authRequired: true,
              title: i18n.global.t('platform.user.users'),
              isAdmin: true
            }
          },
          {
            path: 'users/new',
            name: 'domain-admin-organisation-users-new',
            component: () => import(/* webpackChunkName: "new-user" */ '../views/users/NewUserPage.vue'),
            props: (route) => ({
              organisationId: route.params.organisationId,
              usersPath: `/admin/organisations/${route.params.organisationId}/users`
            }),
            meta: {
              authRequired: true,
              title: i18n.global.t('platform.user.new'),
              isAdmin: true
            }
          },
          {
            path: 'users/:userId',
            name: 'domain-admin-organisation-users-edit',
            component: () => import(/* webpackChunkName: "edit-user" */ '../views/users/EditUserPage.vue'),
            props: (route) => ({
              organisationId: route.params.organisationId,
              userId: route.params.userId,
              usersPath: `/admin/organisations/${route.params.organisationId}/users`
            }),
            meta: {
              authRequired: true,
              title: i18n.global.t('platform.user.edit'),
              isAdmin: true
            }
          },
          {
            path: 'roles',
            name: 'domain-admin-roles',
            component: () => import(/* webpackChunkName: "org-roles" */ '../views/settings/RolesPage.vue'),
            props: true,
            meta: {
              authRequired: true,
              title: i18n.global.t('platform.settings.roles-permissions'),
              isAdmin: true
            }
          },
          {
            path: 'roles/new',
            name: 'domain-admin-new-role',
            component: () => import(/* webpackChunkName: "org-roles" */ '../views/settings/NewRolePage.vue'),
            props: (route) => ({
              organisationId: route.params.organisationId,
              rolesPath: `/admin/organisations/${route.params.organisationId}/roles`
            }),
            meta: {
              authRequired: true,
              title: i18n.global.t('platform.settings.roles-permissions'),
              isAdmin: true
            }
          },
          {
            path: 'roles/:roleId',
            name: 'domain-admin-edit-role',
            component: () => import(/* webpackChunkName: "role" */ '../views/settings/RolePage.vue'),
            props: true,
            meta: {
              authRequired: true,
              title: i18n.global.t('platform.role.roles'),
              isAdmin: true
            }
          },
          {
            path: 'roles/:roleId/users',
            name: 'domain-admin-role-users',
            component: () => import(/* webpackChunkName: "role" */ '../views/settings/RoleUsersPage.vue'),
            props: true,
            meta: {
              authRequired: true,
              title: i18n.global.t('platform.role.roles'),
              isAdmin: true
            }
          },
          {
            path: 'roles/:roleId/users/:userId',
            name: 'domain-admin-role-user',
            component: () => import(/* webpackChunkName: "edit-user" */ '../views/users/EditUserPage.vue'),
            props: (route) => ({
              organisationId: route.params.organisationId,
              userId: route.params.userId,
              usersPath: `/admin/organisations/${route.params.organisationId}/roles/${route.params.roleId}/users`
            }),
            meta: {
              authRequired: true,
              title: i18n.global.t('platform.user.edit'),
              isAdmin: true
            }
          },
          {
            path: 'devices',
            component: () => import(/* webpackChunkName: "org-devices" */ '../views/settings/DevicesPage.vue'),
            props: true,
            name: 'domain-admin-devices',
            meta: {
              authRequired: true,
              title: i18n.global.t('platform.settings.devices'),
              isAdmin: true
            }
          },
          {
            path: 'devices/new',
            name: 'domain-admin-new-device',
            component: () => import(/* webpackChunkName: "org-devices" */ '../views/settings/NewDevicePage.vue'),
            props: true,
            meta: {
              authRequired: true,
              title: i18n.global.t('platform.settings.devices'),
              isAdmin: true
            }
          },
          {
            path: 'devices/:deviceId',
            name: 'domain-admin-edit-device',
            component: () => import(/* webpackChunkName: "org-devices" */ '../views/settings/DevicePage.vue'),
            props: true,
            meta: {
              authRequired: true,
              title: i18n.global.t('platform.settings.devices'),
              isAdmin: true
            }
          },
          {
            path: 'labels',
            name: 'domain-admin-organisation-labels',
            component: () => import(/* webpackChunkName: "private-labels" */ '../views/admin/PrivateLabelsPage.vue'),
            props: true,
            meta: {
              authRequired: true,
              title: i18n.global.t('platform.settings.private-label'),
              isAdmin: true,
              feature: FEATURES.WEBSHOP
            }
          },
          {
            path: 'labels/new',
            name: 'private-labels-new',
            component: () => import(/* webpackChunkName: "new-private-label" */ '../views/admin/privateLabels/NewPrivateLabelPage.vue'),
            props: true,
            meta: {
              authRequired: true,
              title: i18n.global.t('platform.domain-admin.labels.new-private-label'),
              isAdmin: true,
              feature: FEATURES.WEBSHOP
            }
          },
          {
            path: 'labels/:privateLabelId/edit',
            name: 'private-labels-edit',
            component: () => import(/* webpackChunkName: "edit-private-label" */ '../views/admin/privateLabels/EditPrivateLabelPage.vue'),
            props: true,
            meta: {
              authRequired: true,
              title: i18n.global.t('platform.domain-admin.labels.edit-private-label'),
              isAdmin: true,
              feature: FEATURES.WEBSHOP
            }
          }
        ]
      },
      {
        path: 'legal',
        name: 'domain-admin-legal',
        component: () => import(/* webpackChunkName: "legal" */ '../views/legal/LegalPage.vue'),
        meta: { authRequired: true, isAdmin: true, title: i18n.global.t('platform.legal.title') },
        props: (route) => ({
          routePrefix: 'domain-admin-',
          showLayout: false,
          region: route.params.region
        }),
        children: [
          {
            path: ':region/privacy-policy',
            name: 'domain-admin-legal-privacy-policy',
            component: () => import(/* webpackChunkName: "legal-privacy-policy" */ '../views/legal/PrivacyPolicyPage.vue'),
            meta: {
              isAdmin: true,
              authRequired: true,
              title: i18n.global.t('platform.legal.privacy-policy.title')
            }
          },
          {
            path: ':region/terms-of-service',
            name: 'domain-admin-legal-terms-service',
            component: () => import(/* webpackChunkName: "legal-terms-service" */ '../views/legal/TermsServicePage.vue'),
            meta: { isAdmin: true, authRequired: true, title: i18n.global.t('platform.legal.terms-service.title') }
          }
        ]
      }
    ]
  },
  {
    path: '/lens-support',
    name: 'lens-support',
    component: () => import(/* webpackChunkName: "lens-support" */ '@/custom/menicon/views/lens-support/LensSupportLayout.vue'),
    meta: { authRequired: true, isLensSupport: true },
    redirect: '/lens-support/requests',
    children: [
      {
        path: 'requests',
        name: 'lens-support-requests',
        component: () => import(/* webpackChunkName: "requests" */ '@/custom/menicon/views/lens-support/RequestsPage.vue'),
        meta: { authRequired: true, title: i18n.global.t('custom.menicon.lens-support.requests'), isLensSupport: true }
      },
      {
        path: 'account-settings',
        name: 'lens-support-settings',
        component: () => import(/* webpackChunkName: "settings" */ '../views/settings/SettingsPage.vue'),
        meta: { authRequired: true, isLensSupport: true },
        redirect: '/lens-support/account-settings/profile',
        children: [
          {
            path: 'profile',
            name: 'lens-support-profile',
            component: () => import(/* webpackChunkName: "profile" */ '../views/settings/ProfilePage.vue'),
            meta: {
              authRequired: true,
              title: i18n.global.t('platform.settings.profile'),
              isUserSettings: true,
              isLensSupport: true
            }
          },
          {
            path: 'account-setting',
            name: 'lens-support-account-setting',
            component: () => import(/* webpackChunkName: "account-setting" */ '../views/settings/AccountSettingPage.vue'),
            meta: {
              authRequired: true,
              title: i18n.global.t('platform.settings.account-setting'),
              isUserSettings: true,
              isLensSupport: true
            }
          }
        ]
      },
      {
        path: 'legal',
        name: 'lens-support-legal',
        component: () => import(/* webpackChunkName: "legal" */ '../views/legal/LegalPage.vue'),
        meta: { authRequired: true, isLensSupport: true, title: i18n.global.t('platform.legal.title') },
        props: (route) => ({
          routePrefix: 'lens-support-',
          showLayout: false,
          region: route.params.region
        }),
        children: [
          {
            path: ':region/privacy-policy',
            name: 'lens-support-legal-privacy-policy',
            component: () => import(/* webpackChunkName: "legal-privacy-policy" */ '../views/legal/PrivacyPolicyPage.vue'),
            meta: {
              isLensSupport: true,
              authRequired: true,
              title: i18n.global.t('platform.legal.privacy-policy.title')
            }
          },
          {
            path: ':region/terms-of-service',
            name: 'lens-support-legal-terms-service',
            component: () => import(/* webpackChunkName: "legal-terms-service" */ '../views/legal/TermsServicePage.vue'),
            meta: { isLensSupport: true, authRequired: true, title: i18n.global.t('platform.legal.terms-service.title') }
          }
        ]
      }
    ]
  },
  {
    path: '/lens-support/:requestId',
    name: 'lens-support-request',
    component: () => import(/* webpackChunkName: "lens-support" */ '@/custom/menicon/views/lens-support/LensSupportRequestLayout.vue'),
    meta: { authRequired: true, isLensSupport: true, props: true },
    props: true,
    redirect: (to) => ({
      name: 'lens-support-request-prescriptions',
      params: { requestId: to.params.requestId }
    }),
    children: [
      {
        path: 'scans',
        name: 'lens-support-request-scans',
        props: true,
        component: () => import(/* webpackChunkName: "patient-scans" */ '../views/patient/PatientScansPage.vue'),
        meta: { authRequired: true, title: i18n.global.t('platform.patient.scans'), isLensSupport: true }
      },
      {
        path: 'prescription',
        name: 'lens-support-request-prescriptions',
        props: true,
        component: () => import(/* webpackChunkName: "patient-scans" */ '../custom/menicon/views/lens-support/LensSupportPrescriptionPage.vue'),
        meta: { authRequired: true, title: i18n.global.t('custom.menicon.prescriptions.prescription'), isLensSupport: true }
      },
      {
        path: 'prescription-history',
        name: 'lens-support-request-prescription-history',
        component: () =>
          import(
            /* webpackChunkName: "menicon-prescription-history" */ '../custom/menicon/views/patient/PrescriptionHistoryPage.vue'
          ),
        props: true,
        meta: {
          authRequired: true,
          title: i18n.global.t('custom.menicon.prescriptions.history.title'),
          isLensSupport: true
        }
      },
      {
        path: 'prescription/:encounterId',
        name: 'lens-support-request-encounter',
        component: () =>
          import(/* webpackChunkName: "menicon-prescription" */ '../custom/menicon/views/encounter/EncounterPageLoader.vue'),
        props: true,
        meta: {
          authRequired: true,
          isLensSupport: true,
          title: i18n.global.t('custom.menicon.prescriptions.patient-prescriptions')
        }
      }
    ]
  },
  {
    path: '/account-settings',
    name: 'settings',
    component: () => import(/* webpackChunkName: "settings" */ '../views/settings/SettingsPage.vue'),
    meta: { authRequired: true },
    redirect: '/account-settings/profile',
    props: {
      showLayout: true
    },
    children: [
      {
        path: 'profile',
        name: 'profile',
        component: () => import(/* webpackChunkName: "profile" */ '../views/settings/ProfilePage.vue'),
        meta: {
          authRequired: true,
          title: i18n.global.t('platform.settings.profile'),
          isUserSettings: true
        }
      },
      {
        path: 'account-setting',
        name: 'account-setting',
        component: () => import(/* webpackChunkName: "account-setting" */ '../views/settings/AccountSettingPage.vue'),
        meta: {
          authRequired: true,
          title: i18n.global.t('platform.settings.account-setting'),
          isUserSettings: true
        }
      }
    ]
  },
  {
    path: '/organisations/:organisationId',
    component: () => import(/* webpackChunkName: "settings" */ '../views/organisation/OrganisationLayout.vue'),
    meta: { authRequired: true },
    props: true,
    children: [
      {
        path: 'organisation-settings',
        name: 'organisation-settings',
        component: () => import(/* webpackChunkName: "settings" */ '../views/settings/OrganisationSettingsPage.vue'),
        meta: { authRequired: true },
        children: [
          {
            path: 'users',
            name: 'settings-users',
            component: () => import(/* webpackChunkName: "users" */ '../views/users/UsersPage.vue'),
            meta: {
              authRequired: true,
              title: i18n.global.t('platform.settings.users'),
              permission: 'user:read'
            }
          },
          {
            path: 'users/new',
            name: 'settings-users-new',
            component: () => import(/* webpackChunkName: "new-user" */ '../views/users/NewUserPage.vue'),
            props: (route) => ({
              usersPath: `/organisations/${route.params.organisationId}/organisation-settings/users`
            }),
            meta: {
              authRequired: true,
              title: i18n.global.t('platform.user.new'),
              permission: 'user:create'
            }
          },
          {
            path: 'users/:userId',
            name: 'settings-users-edit',
            component: () => import(/* webpackChunkName: "edit-user" */ '../views/users/EditUserPage.vue'),
            props: (route) => ({
              userId: route.params.userId,
              usersPath: `/organisations/${route.params.organisationId}/organisation-settings/users`
            }),
            meta: {
              authRequired: true,
              title: i18n.global.t('platform.user.edit'),
              permission: 'user:update'
            }
          },
          {
            path: 'roles',
            name: 'settings-roles',
            component: () => import(/* webpackChunkName: "role" */ '../views/settings/RolesPage.vue'),
            props: true,
            meta: {
              authRequired: true,
              title: i18n.global.t('platform.role.roles'),
              permission: 'role:read'
            }
          },
          {
            path: 'roles/new',
            name: 'settings-new-role',
            component: () => import(/* webpackChunkName: "new-role" */ '../views/settings/NewRolePage.vue'),
            props: (route) => ({
              rolesPath: `/organisations/${route.params.organisationId}/organisation-settings/roles`,
              organisationId: route.params.organisationId
            }),
            meta: {
              authRequired: true,
              title: i18n.global.t('platform.role.new'),
              permission: 'role:manage-roles-permissions'
            }
          },
          {
            path: 'roles/:roleId',
            name: 'settings-edit-role',
            component: () => import(/* webpackChunkName: "role" */ '../views/settings/RolePage.vue'),
            props: true,
            meta: {
              authRequired: true,
              title: i18n.global.t('platform.role.edit'),
              permission: 'role:read'
            }
          },
          {
            path: 'roles/:roleId/users',
            name: 'settings-role-users',
            component: () => import(/* webpackChunkName: "role-users" */ '../views/settings/RoleUsersPage.vue'),
            props: true,
            meta: {
              authRequired: true,
              title: i18n.global.t('platform.role.roles'),
              permission: 'role:read'
            }
          },
          {
            path: 'roles/:roleId/users/:userId',
            name: 'settings-role-user',
            component: () => import(/* webpackChunkName: "edit-user" */ '../views/users/EditUserPage.vue'),
            props: (route) => ({
              userId: route.params.userId,
              usersPath: `/organisations/${route.params.organisationId}/organisation-settings/users`
            }),
            meta: {
              authRequired: true,
              title: i18n.global.t('platform.user.edit'),
              permission: 'user:update'
            }
          },
          {
            path: 'devices',
            name: 'settings-devices',
            component: () => import(/* webpackChunkName: "device" */ '../views/settings/DevicesPage.vue'),
            props: true,
            meta: {
              authRequired: true,
              title: i18n.global.t('platform.settings.devices'),
              permission: 'device:read'
            }
          },
          {
            path: 'devices/new',
            name: 'settings-new-device',
            component: () => import(/* webpackChunkName: "device" */ '../views/settings/NewDevicePage.vue'),
            props: true,
            meta: {
              authRequired: true,
              title: i18n.global.t('platform.settings.devices'),
              permission: 'device:create'
            }
          },
          {
            path: 'devices/:deviceId',
            component: () => import(/* webpackChunkName: "device" */ '../views/settings/DevicePage.vue'),
            name: 'settings-edit-device',
            props: true,
            meta: {
              authRequired: true,
              title: i18n.global.t('platform.settings.devices'),
              permission: 'device:read'
            }
          },
          {
            path: 'preferences',
            name: 'settings-preferences',
            component: () =>
              import(/* webpackChunkName: "org-settings" */ '../views/admin/OrganisationPreferencesPage.vue'),
            props: true,
            meta: {
              authRequired: true,
              title: i18n.global.t('platform.settings.preferences'),
              permission: 'org-unit-preference:update'
            }
          },
          {
            path: 'orders',
            name: 'orders-history',
            component: () => import(/* webpackChunkName: "order-history" */ '../views/settings/OrderHistory.vue'),
            props: true,
            meta: {
              authRequired: true,
              title: i18n.global.t('platform.settings.order-history')
            }
          },
          {
            path: 'orders/:orderId',
            name: 'order-detail',
            component: () => import('../views/settings/OrderDetailWrapper.vue'),
            props: true,
            meta: {
              authRequired: true,
              title: i18n.global.t('platform.webshop.order-detail')
            }
          }
        ]
      },
      {
        path: 'account-settings',
        name: 'organisation-user-settings',
        component: () => import(/* webpackChunkName: "settings" */ '../views/settings/SettingsPage.vue'),
        meta: { authRequired: true },
        redirect: (to) => ({ name: 'organisation-user-profile', params: { organisationId: to.params.organisationId } }),
        children: [
          {
            path: 'profile',
            name: 'organisation-user-profile',
            component: () => import(/* webpackChunkName: "profile" */ '../views/settings/ProfilePage.vue'),
            meta: {
              authRequired: true,
              title: i18n.global.t('platform.settings.profile'),
              isUserSettings: true
            }
          },
          {
            path: 'account-setting',
            name: 'organisation-user-account-setting',
            component: () => import(/* webpackChunkName: "account-setting" */ '../views/settings/AccountSettingPage.vue'),
            meta: {
              authRequired: true,
              title: i18n.global.t('platform.settings.account-setting'),
              isUserSettings: true
            }
          }
        ]
      },
      {
        path: 'participants',
        name: 'participant-list',
        component: () => import(/* webpackChunkName: "participants" */ '../custom/research/views/ParticipantListPage.vue'),
        meta: {
          authRequired: true,
          title: i18n.global.t('platform.navigation.participants'),
          permission: 'anonymous-participant:read'
        }
      },
      {
        path: 'participants/new',
        name: 'new-participant',
        component: () => import(/* webpackChunkName: "new-participant" */ '../custom/research/views/NewParticipant.vue'),
        meta: {
          authRequired: true,
          title: i18n.global.t('platform.participant.new'),
          permission: 'anonymous-participant:create'
        }
      },
      {
        path: 'patients',
        name: 'patient-list',
        component: () => import(/* webpackChunkName: "patients" */ '../views/patient/PatientListPage.vue'),
        props: true,
        meta: {
          authRequired: true,
          title: i18n.global.t('platform.navigation.patients'),
          permission: 'patient:read'
        }
      },
      {
        path: 'patients/new',
        name: 'patient-new',
        component: () => import(/* webpackChunkName: "new-patient" */ '../views/patient/NewPatientPage.vue'),
        meta: {
          authRequired: true,
          title: i18n.global.t('platform.patient.new'),
          permission: 'patient:create'
        }
      },
      {
        path: 'webshop',
        name: 'webshop',
        component: () => import('@/views/webshop/WebshopPage.vue'),
        props: (route) => ({
          showLayout: false,
          organisationId: route.params.organisationId
        }),
        meta: {
          authRequired: true,
          title: i18n.global.t('platform.webshop.title'),
          permission: 'webshop:read',
          feature: FEATURES.WEBSHOP
        },
        children: [
          {
            path: ':categoryslug',
            name: 'category',
            component: () => import('@/views/webshop/ProductCatalog.vue'),
            meta: {
              authRequired: true,
              permission: 'webshop:read',
              feature: FEATURES.WEBSHOP
            }
          },
          {
            path: 'cart',
            name: 'webshop-cart',
            component: () => import('@/views/webshop/WebshopCart.vue'),
            meta: {
              authRequired: true,
              title: i18n.global.t('platform.webshop.cart-title'),
              permission: 'webshop:read',
              feature: FEATURES.WEBSHOP
            }
          }
        ]
      },
      {
        path: 'dashboard',
        name: 'dashboard',
        component: () => import('../views/dashboard/DashboardPage.vue'),
        props: true,
        meta: {
          authRequired: true,
          title: i18n.global.t('platform.navigation.dashboard'),
          permission: 'dashboard:read',
          feature: FEATURES.DASHBOARD
        }
      },
      {
        path: 'webshop/404',
        name: 'webshop-404',
        component: () => import('@/views/webshop/WebshopPage.vue'),
        props: (route) => ({
          showLayout: false,
          organisationId: route.params.organisationId
        }),
        meta: {
          authRequired: true,
          title: i18n.global.t('platform.webshop.title'),
          permission: 'webshop:read',
          feature: FEATURES.WEBSHOP
        }
      },
      {
        path: 'legal',
        name: 'organisation-legal',
        component: () => import(/* webpackChunkName: "legal" */ '../views/legal/LegalPage.vue'),
        meta: { authRequired: true, title: i18n.global.t('platform.legal.title') },
        props: (route) => ({
          routePrefix: 'organisation-',
          showLayout: false,
          organisationId: route.params.organisationId,
          region: route.params.region
        }),
        children: [
          {
            path: ':region/privacy-policy',
            name: 'organisation-legal-privacy-policy',
            component: () => import(/* webpackChunkName: "legal-privacy-policy" */ '../views/legal/PrivacyPolicyPage.vue'),
            props: true,
            meta: {
              authRequired: true,
              title: i18n.global.t('platform.legal.privacy-policy.title')
            }
          },
          {
            path: ':region/terms-of-service',
            name: 'organisation-legal-terms-service',
            component: () => import(/* webpackChunkName: "legal-terms-service" */ '../views/legal/TermsServicePage.vue'),
            props: true,
            meta: { authRequired: true, title: i18n.global.t('platform.legal.terms-service.title') }
          }
        ]
      }
    ]
  },
  {
    path: '/organisations/:organisationId/participants/:participantId',
    component: () => import(/* webpackChunkName: "participant" */ '../custom/research/views/ParticipantPage.vue'),
    name: 'participant',
    props: true,
    redirect: (to) => ({
      name: 'participant-forms',
      params: { participantId: to.params.participantId, organisationId: to.params.organisationId }
    }),
    meta: {
      authRequired: true,
      title: i18n.global.t('platform.participant.participant'),
      permission: 'anonymous-participant:read'
    },
    children: [
      {
        path: 'details',
        name: 'participant-details',
        component: () =>
          import(/* webpackChunkName: "participant-details" */ '../custom/research/views/ParticipantDetailsPage.vue'),
        props: true,
        meta: {
          authRequired: true,
          title: i18n.global.t('platform.participant.details'),
          permission: 'anonymous-participant:read'
        }
      },
      {
        path: 'forms',
        name: 'participant-forms',
        component: () =>
          import(
            /* webpackChunkName: "participant-form-submissions" */ '../views/patient/PatientFormSubmissionsPage.vue'
          ),
        props: true,
        meta: { authRequired: true, title: i18n.global.t('platform.settings.forms') }
      },
      {
        path: 'forms/:formSubmissionId',
        name: 'participant-form',
        component: () =>
          import(
            /* webpackChunkName: "participant-form-submission" */ '../views/patient/PatientFormSubmissionPage.vue'
          ),
        props: true,
        meta: { authRequired: true, title: i18n.global.t('platform.form.form') }
      }
    ]
  },
  {
    path: '/organisations/:organisationId/patients/:patientId',
    component: () => import(/* webpackChunkName: "patient" */ '../views/patient/PatientPage.vue'),
    name: 'patient',
    props: true,
    redirect: (to) => ({
      name: 'patient-details',
      params: { patientId: to.params.patientId, organisationId: to.params.organisationId }
    }),
    meta: {
      authRequired: true,
      title: i18n.global.t('platform.patient.patient'),
      permission: 'patient:read'
    },
    children: [
      {
        path: 'prescriptions',
        name: 'patient-prescriptions',
        component: () =>
          import(
            /* webpackChunkName: "menicon-prescriptions" */ '../custom/menicon/views/patient/PrescriptionsPage.vue'
          ),
        props: true,
        meta: {
          authRequired: true,
          title: i18n.global.t('custom.menicon.prescriptions.patient-prescriptions'),
          permission: 'menicon-prescription:manage'
        }
      },
      {
        path: 'prescriptions/myopia-prognosis',
        name: 'myopia-prognosis',
        component: () =>
          import(
            /* webpackChunkName: "menicon-prescriptions" */ '../custom/menicon/views/patient/MyopiaPrognosisPage.vue'
          ),
        props: true,
        meta: {
          authRequired: true,
          title: i18n.global.t('custom.menicon.myopia-prognosis.title'),
          permission: 'menicon-prescription:manage',
          feature: FEATURES.MYOPIA_PROGNOSIS
        }
      },
      {
        path: 'prescriptions/:journeyId',
        name: 'prescription-details',
        component: () =>
          import(
            /* webpackChunkName: "menicon-prescriptions" */ '../custom/menicon/views/patient/PrescriptionDetailsPage.vue'
          ),
        props: true,
        meta: {
          authRequired: true,
          title: i18n.global.t('custom.menicon.prescriptions.patient-prescriptions'),
          permission: 'menicon-prescription:manage'
        }
      },
      {
        path: 'prescriptions/:journeyId/history',
        name: 'prescription-history',
        component: () =>
          import(
            /* webpackChunkName: "menicon-prescription-history" */ '../custom/menicon/views/patient/PrescriptionHistoryPage.vue'
          ),
        props: true,
        meta: {
          authRequired: true,
          title: i18n.global.t('custom.menicon.prescriptions.history.title'),
          permission: 'menicon-prescription:manage'
        }
      },
      {
        path: 'prescriptions/:journeyId/encounters/:encounterId',
        name: 'patient-encounter',
        component: () =>
          import(/* webpackChunkName: "menicon-prescription" */ '../custom/menicon/views/encounter/EncounterPageLoader.vue'),
        props: true,
        meta: {
          authRequired: true,
          title: i18n.global.t('custom.menicon.prescriptions.patient-prescriptions'),
          permission: 'menicon-prescription:manage'
        }
      },
      {
        path: 'details',
        name: 'patient-details',
        component: () => import(/* webpackChunkName: "patient-details" */ '../views/patient/PatientDetailsPage.vue'),
        props: true,
        meta: {
          authRequired: true,
          title: i18n.global.t('platform.patient.details'),
          permission: 'patient:read'
        }
      },
      {
        path: 'scans',
        name: 'patient-scans',
        component: () => import(/* webpackChunkName: "patient-scans" */ '../views/patient/PatientScansPage.vue'),
        props: true,
        meta: {
          authRequired: true,
          title: i18n.global.t('platform.patient.scans'),
          permission: 'patent-scans:read'
        }
      },
      {
        path: 'virtual-doctor',
        name: 'virtual-doctor',
        component: () => import(/* webpackChunkName: "virtual-doctor" */ '../custom/menicon/layouts/VirtualDoctorLayout.vue'),
        redirect: (to) => ({
          name: 'patient-survey-results',
          params: { patientId: to.params.patientId, organisationId: to.params.organisationId }
        }),
        props: true,
        meta: { authRequired: true, title: i18n.global.t('custom.menicon.virtual-doctor.title') },
        children: [
          {
            path: 'survey-results',
            name: 'patient-survey-results',
            component: () =>
              import(
                /* webpackChunkName: "virtual-doctor-list" */ '../custom/menicon/views/patient/virtual-doctor/VirtualDoctorSurveyResultsPage.vue'
              ),
            props: true,
            meta: {
              authRequired: true,
              title: i18n.global.t('custom.menicon.virtual-doctor.survey-results'),
              permission: 'menicon-virtual-doctor:read'
            }
          },
          {
            path: 'instructions',
            name: 'vd-instructions',
            component: () =>
              import(
                /* webpackChunkName: "virtual-doctor-list" */ '../custom/menicon/views/patient/virtual-doctor/VirtualDoctorInstructions.vue'
              ),
            props: true,
            meta: {
              authRequired: true,
              title: i18n.global.t('custom.menicon.virtual-doctor.survey-results'),
              permission: 'virtual-doctor-instructions:read'
            }
          },
          {
            path: 'survey-results/:surveyResultId',
            name: 'patient-survey',
            component: () =>
              import(
                /* webpackChunkName: "virtual-doctor-single" */ '../custom/menicon/views/patient/virtual-doctor/VirtualDoctorSurveyResultPage.vue'
              ),
            props: true,
            meta: {
              authRequired: true,
              title: i18n.global.t('custom.menicon.virtual-doctor.survey-result'),
              permission: 'menicon-virtual-doctor:read'
            }
          },
          {
            name: 'patient-messages',
            path: 'messages',
            component: () =>
              import(
                /* webpackChunkName: "virtual-doctor-list" */ '../custom/menicon/views/patient/virtual-doctor/VirtualDoctorMessages.vue'
              ),
            props: true,
            meta: {
              authRequired: true,
              title: i18n.global.t('custom.menicon.virtual-doctor.vd-messages'),
              permission: 'menicon-virtual-doctor-messaging:organisational-unit'
            }
          }
        ]
      },
      {
        path: 'orders',
        name: 'webshop-orders',
        component: () => import('../views/patient/WebshopOrders.vue'),
        props: true,
        meta: {
          authRequired: true,
          title: i18n.global.t('platform.patient.details'),
          permission: 'patient:read'
        }
      },
      {
        path: 'orders/:orderId',
        name: 'webshop-order-detail',
        component: () => import('../views/patient/WebshopOrderDetails.vue'),
        props: true,
        meta: {
          authRequired: true,
          title: i18n.global.t('platform.webshop.order-detail'),
          feature: FEATURES.WEBSHOP,
          permission: 'webshop:read'
        }
      }
    ]
  },
  {
    path: '/legal',
    name: 'legal',
    component: () => import(/* webpackChunkName: "legal" */ '../views/legal/LegalPage.vue'),
    meta: { authRequired: true, title: i18n.global.t('platform.legal.title') },
    props: true,
    children: [
      {
        path: ':region/privacy-policy',
        name: 'legal-privacy-policy',
        component: () => import(/* webpackChunkName: "legal-privacy-policy" */ '../views/legal/PrivacyPolicyPage.vue'),
        meta: {
          authRequired: true,
          title: i18n.global.t('platform.legal.privacy-policy.title')
        }
      },
      {
        path: ':region/terms-of-service',
        name: 'legal-terms-service',
        component: () => import(/* webpackChunkName: "legal-terms-service" */ '../views/legal/TermsServicePage.vue'),
        meta: { authRequired: true, title: i18n.global.t('platform.legal.terms-service.title') }
      }
    ]
  },
  {
    path: '/unsupported-browser',
    name: 'unsupported-browser',
    component: UnsupportedBrowser,
    meta: { title: i18n.global.t('platform.error.unsupported-browser.title') }
  },
  {
    name: '404',
    path: '/404',
    component: Error404,
    meta: { title: '404' }
  },
  {
    name: '503',
    path: '/503',
    component: Error503,
    meta: { title: '503' }
  },
  {
    name: '401',
    path: '/401',
    component: Error401,
    meta: { title: '401' }
  },
  {
    name: 'catch-all',
    path: '/:pathMatch(.*)*',
    component: Error404,
    meta: { title: '404' }
  }
];

export default routes;
